import qs from "qs";

import { AppConfig } from "~/AppConfig";

import { createSecurityWorker } from "../api/workers";
import { Api, ApiConfig, HttpClient, QueryParamsType } from "./Api";

type SecurityDataType = unknown;
export const API_PATH = "/api/ui/v1";
export type ApiClient = Api<SecurityDataType>;

/**
 * Custom fetch wrapper to safely handle multipart/form-data uploads.
 *
 * Why this is needed:
 * The generated `HttpClient` automatically sets the `Content-Type: multipart/form-data` header
 * whenever the request type is `ContentType.FormData`. However, browsers normally generate a
 * unique `boundary` string when they build multipart requests — it’s required to separate file parts.
 *
 * When the `Content-Type` is set manually without a boundary (e.g. just `multipart/form-data`),
 * the browser skips adding the boundary entirely. As a result:
 *   - The request body is sent as a raw byte stream with no separators.
 *   - Rails (or any backend framework) cannot parse it as multipart data.
 *   - File fields arrive as large text blobs instead of proper `ActionDispatch::Http::UploadedFile` objects.
 *
 * Solution:
 * This wrapper intercepts all requests, checks if the body is a FormData instance,
 * and removes any manually-set `Content-Type` header. That allows the browser to:
 *   - Automatically generate and append the correct `boundary` string.
 *   - Properly encode the request body as multipart/form-data.
 *   - Let the backend correctly parse and receive uploaded files.
 *
 * The rest of the request (headers, tokens, etc.) is preserved.
 */
const safeMultipartFetch: typeof fetch = (input, init = {}) => {
  // Detect FormData body
  if (
    init &&
    typeof FormData !== "undefined" &&
    init.body instanceof FormData
  ) {
    const h = init.headers as HeadersInit | undefined;

    // Remove manually-set Content-Type headers in all possible forms
    if (h instanceof Headers) {
      h.delete("Content-Type");
    } else if (Array.isArray(h)) {
      init.headers = h.filter(([k]) => k.toLowerCase() !== "content-type");
    } else if (h && typeof h === "object") {
      const headersObj = { ...(h as Record<string, string>) };
      delete headersObj["Content-Type"];
      delete headersObj["content-type"];
      init.headers = headersObj;
    }
  }

  return fetch(input, init);
};

const HttpParams = {
  baseApiParams: {
    secure: true,
    format: "json",
  },
  securityWorker: createSecurityWorker(),
  baseUrl: AppConfig.API_URL + API_PATH,
  customFetch: safeMultipartFetch,
} satisfies ApiConfig;

// @ts-expect-error override toQueryString
HttpClient.prototype.toQueryString = (rawQuery?: QueryParamsType): string => {
  return rawQuery
    ? qs.stringify(rawQuery, {
        arrayFormat: "brackets",
        skipNulls: true,
      })
    : "";
};

export const HttpApiClientWithParams = new HttpClient(HttpParams);

export const apiClient = new Api(HttpApiClientWithParams);
